import { appConfig } from '@/config/app.config';
import { httpClient } from '@/services/http/http-client';
import type { Page, PageRequest } from '@/types/api.types';

import {
  type Invoice,
  type InvoiceCollections,
  invoiceCollectionsSchema,
  type InvoiceDetail,
  invoiceDetailSchema,
  invoiceSchema,
  type InvoiceStatus,
  type InvoiceSummary,
  invoiceSummarySchema,
  type InvoiceWithMember,
  invoiceWithMemberSchema,
  type PaymentStateFilter,
  type RecordPaymentFormInput,
  shareLinkSchema,
} from '../schemas/invoice.schema';

/** The only place this feature talks to the network. */

/** The window the summary's "collected" figure covers, as `YYYY-MM-DD` strings. */
export interface CollectedRange {
  from: string;
  to: string;
}

/** The Bills totals' range. Either end may be left out; both out means all time. */
export interface CollectionsRange {
  from?: string | undefined;
  to?: string | undefined;
}

export interface InvoiceListFilter extends PageRequest {
  q?: string | undefined;
  memberId?: number | undefined;
  status?: InvoiceStatus | undefined;
  paymentState?: PaymentStateFilter | undefined;
  from?: string | undefined;
  to?: string | undefined;
}

export function listInvoices(
  filter: InvoiceListFilter,
  signal?: AbortSignal,
): Promise<Page<InvoiceWithMember>> {
  return httpClient.getPage('/invoices', {
    schema: invoiceWithMemberSchema,
    query: { ...filter },
    signal,
  });
}

export function listMemberInvoices(
  memberId: number,
  page: PageRequest,
  signal?: AbortSignal,
): Promise<Page<Invoice>> {
  return httpClient.getPage(`/members/${String(memberId)}/invoices`, {
    schema: invoiceSchema,
    query: { ...page },
    signal,
  });
}

export function getInvoice(id: number, signal?: AbortSignal): Promise<InvoiceDetail> {
  return httpClient.get(`/invoices/${String(id)}`, {
    schema: invoiceDetailSchema,
    ...(signal ? { signal } : {}),
  });
}

/**
 * Money still due across every open bill, and what came in over `range` — this calendar month
 * when the range is left out.
 */
export function getInvoiceSummary(
  range?: CollectedRange,
  signal?: AbortSignal,
): Promise<InvoiceSummary> {
  return httpClient.get('/invoices/summary', {
    schema: invoiceSummarySchema,
    ...(range ? { query: { ...range } } : {}),
    ...(signal ? { signal } : {}),
  });
}

/** Money received over `range` — all time when both ends are left out — split cash / online / other. */
export function getInvoiceCollections(
  range: CollectionsRange,
  signal?: AbortSignal,
): Promise<InvoiceCollections> {
  return httpClient.get('/invoices/collections', {
    schema: invoiceCollectionsSchema,
    query: { ...range },
    ...(signal ? { signal } : {}),
  });
}

/** The bill as an A4 PDF, rendered by the server, with the file name the server chose for it. */
export function getInvoicePdf(
  id: number,
  signal?: AbortSignal,
): Promise<{ blob: Blob; fileName: string | null }> {
  return httpClient.getBlob(`/invoices/${String(id)}/pdf`, { signal });
}

/** Gets (or makes) the token behind the bill's public link. The same token every time. */
export function createInvoiceShareLink(id: number): Promise<{ token: string }> {
  return httpClient.post(`/invoices/${String(id)}/share-link`, undefined, {
    schema: shareLinkSchema,
  });
}

/**
 * The address a member opens to download their bill — no sign-in. Built on the API's own base URL,
 * so it points wherever the app is talking to.
 */
export function publicBillUrl(token: string): string {
  return new URL(`${appConfig.api.baseUrl}/bills/${token}`, window.location.origin).href;
}

export function recordPayment(
  invoiceId: number,
  input: RecordPaymentFormInput,
): Promise<InvoiceDetail> {
  return httpClient.post(`/invoices/${String(invoiceId)}/payments`, input, {
    schema: invoiceDetailSchema,
  });
}
